import { useState } from "react";
import type { Content } from "@/app/queries";
import { plural } from "@/lib/plural";
import { IconButton, INPUT, type Patch } from "./shared";

type LevelId = "base" | "apply" | "extra";

/** Базовый и применение считаются в гейт (practice_share); «дополнительно» — нет. */
const LEVELS: { id: LevelId; label: string; hint: string; outside?: boolean }[] = [
  { id: "base", label: "Базовый", hint: "закрепление темы" },
  { id: "apply", label: "Применение", hint: "перенос на новые данные" },
  { id: "extra", label: "Дополнительно", hint: "для тех, кто ждёт ментора или хочет больше", outside: true },
];

export function PracticeTab({ content, patch }: { content: Content; patch: (p: Patch) => void }) {
  return (
    <div className="flex flex-col gap-3.5">
      {LEVELS.map((l) => (
        <Level key={l.id} id={l.id} label={l.label} hint={l.hint} outside={!!l.outside} tasks={content.practice[l.id] ?? []} patch={patch} />
      ))}
    </div>
  );
}

function Level({ id, label, hint, outside, tasks, patch }: { id: LevelId; label: string; hint: string; outside: boolean; tasks: Content["practice"]["base"]; patch: (p: Patch) => void }) {
  const [draft, setDraft] = useState("");
  const n = tasks.length;
  const add = () => {
    const text = draft.trim();
    if (!text) return;
    patch((c) => ({ ...c, practice: { ...c.practice, [id]: [...(c.practice[id] ?? []), { id: "", text }] } }));
    setDraft("");
  };
  return (
    <section className={`overflow-hidden rounded-md border bg-card ${outside ? "border-dashed border-bd2" : "border-bd"}`} data-testid={`level-${id}`}>
      <div className="flex items-center gap-2.5 border-b border-bd bg-bg2 px-3.5 py-[11px]">
        <span className="text-[13.5px] font-semibold">{label}</span>
        {outside && <span className="rounded-sm border border-bd2 px-1.5 py-px font-mono text-[10.5px] text-ink2">вне гейта</span>}
        <span className="font-mono text-[11px] text-mut">
          {n} {plural(n, "задача", "задачи", "задач")}
        </span>
        <span className="flex-1" />
        <span className="font-mono text-[11px] text-mut">{hint}</span>
      </div>
      {tasks.map((t, i) => (
        <div key={t.id || `t-${i}`} className="grid grid-cols-[22px_minmax(0,1fr)_26px] items-start gap-3 border-b border-bd px-3.5 py-[11px]">
          <span className="mt-0.5 cursor-grab font-mono text-[12px] text-mut" aria-hidden>
            ⠿
          </span>
          <span className="text-[13px] leading-[1.5] text-ink2 text-pretty">{t.text}</span>
          <IconButton onClick={() => patch((c) => ({ ...c, practice: { ...c.practice, [id]: (c.practice[id] ?? []).filter((_, j) => j !== i) } }))} aria-label={`удалить задачу ${i + 1}`}>
            ×
          </IconButton>
        </div>
      ))}
      {n === 0 && <div className="border-b border-bd px-3.5 py-[11px] text-[12.5px] text-ink2">Задач пока нет — уровень не покажется студенту, пока пуст.</div>}
      {outside && n > 0 && <div className="border-b border-bd px-3.5 py-2 font-mono text-[10.5px] text-mut">не входит в долю задач гейта; студент видит эти задачи ещё и на странице «Дополнительная практика»</div>}
      <form
        className="flex gap-2.5 px-3.5 py-[11px]"
        onSubmit={(e) => {
          e.preventDefault();
          add();
        }}
      >
        <input aria-label={`формулировка задачи · ${label.toLowerCase()}`} placeholder="формулировка задачи" value={draft} onChange={(e) => setDraft(e.target.value)} className={`${INPUT} flex-1 font-sans text-[13px]`} />
        <button type="submit" className="cursor-pointer rounded-sm border border-bd2 bg-transparent px-3.5 py-2 font-mono text-[12px] text-ink hover:border-acc hover:text-acc">
          + задача
        </button>
      </form>
    </section>
  );
}
